/* Masthead — la cabecera compartida por todas las pantallas.
 *
 * Regla de consistencia que viene del producto y el rediseño conserva: las MISMAS pestañas, en
 * el MISMO orden, en todas las páginas, con la actual marcada y `aria-current`. El orden es
 * **Oportunidades primero, Asistente después** — el frontend anterior las tenía al revés en el
 * chat y en el mismo orden en el panel, así que cambiaban de sitio al navegar.
 *
 * A la derecha: la píldora de la empresa activa (qué perfil está cargado, que es la pregunta que
 * más se hace quien usa esto como servicio para varias empresas) y el acceso al perfil.
 */
import { useEffect, useRef, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { borrar } from '../api/cliente'
import { normalizarNombre } from '../api/formato'
import { useSesion } from '../estado/sesion'
import { IconoCandado, IconoDesplegar, IconoMenu } from './Iconos'
import { usarCompacto } from './usarAngosto'
import './Masthead.css'

interface Props {
  /** Razón social de la empresa cuyo perfil está cargado. */
  empresa?: string | null
  /**
   * ¿Hay un perfil guardado? Es distinto de tener NOMBRE.
   *
   * Sin esto la píldora decía «Sin perfil cargado» a quien sí tenía perfil pero no había escrito su
   * razón social — mientras la bandeja, a dos centímetros, le mostraba afinidades calculadas con ese
   * mismo perfil. Dos afirmaciones opuestas en la misma pantalla, y la que se cree es la del
   * masthead, porque es la que parece hablar del estado de la cuenta.
   */
  conPerfil?: boolean
}

export function Masthead({ empresa, conPerfil }: Props) {
  const { yo, puede, recargar } = useSesion()
  /* Por debajo de 640 px la fila no cabe: medido antes de tocar nada, desbordaba 37 px y el
     masthead crecía a 135 px de alto mientras `--alto-masthead` seguía diciendo 62 — o sea que
     TODO lo pegajoso que cuelga de ese token (la franja de veredicto de la ficha, el pie de
     acciones) quedaba anclado por dentro de la propia cabecera. Plegando las pestañas y las
     acciones en un menú, la fila vuelve a medir 44 + 2×9 = 62 y el token deja de mentir. */
  const compacto = usarCompacto()
  const [abierto, setAbierto] = useState(false)
  const botonMenu = useRef<HTMLButtonElement | null>(null)
  const donde = useLocation()

  /* Se cierra al navegar. Un menú que sobrevive al cambio de página queda flotando sobre una
     pantalla que ya no es la suya, y es de los que nadie prueba porque hay que navegar CON él
     abierto para verlo. */
  useEffect(() => setAbierto(false), [donde.pathname])
  // Y al ensanchar: si el menú queda abierto cuando el ☰ desaparece, no hay con qué cerrarlo.
  useEffect(() => { if (!compacto) setAbierto(false) }, [compacto])

  /* `Escape` cierra y DEVUELVE EL FOCO al botón, la misma lección que el acordeón de la bandeja:
     al desmontarse el panel el foco se caería a `body` y quien navega con teclado se queda sin
     punto de partida. */
  useEffect(() => {
    if (!abierto) return
    const alPulsar = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      setAbierto(false)
      botonMenu.current?.focus()
    }
    window.addEventListener('keydown', alPulsar)
    return () => window.removeEventListener('keydown', alPulsar)
  }, [abierto])

  async function salir() {
    // Si el servidor falla, se recarga igual: la sesión pudo haberse revocado antes del error, y
    // dejar la interfaz creyendo que sigue viva es peor que un rebote al ingreso.
    try {
      await borrar('/api/sesion')
    } finally {
      await recargar()
    }
  }

  /* Las pestañas, en una variable porque en compacto cambian de SITIO: se pliegan DENTRO del menú
     en vez de desaparecer. Ocultarlas sin más habría sido borrar navegación, no plegarla — y es lo
     que hacía la primera versión de este bloque, que se vio al medir. Mismo patrón que el panel de
     la bandeja y la cabecera de la ficha: un solo árbol, montado en uno de dos sitios. */
  const pestañas = (
          <nav className="pestañas" aria-label="Secciones">
            <NavLink to="/" end className={clase}>
              Oportunidades
            </NavLink>
            {puede('chat') && (
              <NavLink to="/asistente" className={clase}>
                Asistente
              </NavLink>
            )}
          </nav>
  )

  return (
    <header className="masthead">
      <div className="masthead-fila">
        <NavLink to="/" className="marca">
          <b>Vigía</b>
          <span className="marca-bajada">Contratación pública · Colombia</span>
        </NavLink>

        {!compacto && pestañas}


        {/* El ☰ del documento, solo en compacto. Pliega —no borra— las pestañas y las acciones:
            todo lo que estaba sigue estando, a un toque. El documento NO dibuja el menú abierto,
            así que la forma del panel es una decisión por falta de dibujo: cuelga bajo la
            cabecera, que es lo que menos maquinaria pide (sin velo, sin atrapar el foco) y lo que
            deja el contenido de la página a la vista detrás. */}
        {/* Un envoltorio para el grupo de la derecha. En escritorio reproduce EXACTAMENTE lo de
            antes —el mismo `margin-left: auto` y el mismo hueco de 14 px entre la píldora y los
            enlaces— y en compacto es lo que permite que la píldora y el ☰ se queden en la fila
            mientras el resto se pliega. Sin él, la píldora estaba dentro de lo que se pliega y el
            documento la dibuja visible en los tres cortes. */}
        <div className="masthead-derecha">
          {/* La píldora dice CON QUÉ EMPRESA se está trabajando y lleva a la cartera. Es la
              pregunta que más se hace quien usa esto como servicio para varias, y en el 5a es
              justamente lo que ocupa ese sitio.

              El `title` lleva la razón social ENTERA porque en pantalla va recortada: las de verdad
              llegan a sesenta caracteres («Asociación Afrocolombiana José Domingo Ortiz de El Banco
              Magdalena») y a todo lo ancho empujaban las acciones a un segundo renglón, con lo que
              la cabecera pegajosa crecía de 61 a 85 px y tapaba lo que hay debajo. */}
          {empresa ? (
            <NavLink to="/empresas" className="empresa-activa"
                     title={`Trabajando con ${empresa} · ver tus empresas`}>
              <i aria-hidden="true" />
              <span className="empresa-nombre">{normalizarNombre(empresa)}</span>
              {/* El «▾» del documento. Va como SVG: un glifo suelto depende de la fuente del
                  sistema y cambia de peso y de tamaño entre teléfonos. */}
              <IconoDesplegar className="empresa-desplegar" />
            </NavLink>
          ) : conPerfil ? (
            <NavLink to="/perfil" className="empresa-vacia" title="Tu perfil no tiene razón social">
              Perfil sin nombre
            </NavLink>
          ) : (
            <NavLink to="/perfil" className="empresa-vacia">
              Sin perfil cargado
            </NavLink>
          )}

          {compacto && (
            <button type="button" className="masthead-boton" ref={botonMenu}
                    aria-expanded={abierto} aria-controls="masthead-menu"
                    onClick={() => setAbierto((v) => !v)}>
              <IconoMenu />
              <span className="oculto-visual">{abierto ? 'Cerrar el menú' : 'Abrir el menú'}</span>
            </button>
          )}

        <div className={`masthead-acciones${compacto && abierto ? ' abierto' : ''}`}
             id="masthead-menu">
            {compacto && pestañas}
          <NavLink to="/empresas" className={clase}>
            Tus empresas
          </NavLink>
          {yo?.es_admin && (
            <NavLink to="/administracion" className={clase}>
              Administración
            </NavLink>
          )}
          {yo && (
            <span className="quien" title={yo.usuario}>
              {yo.nombre}
              {yo.alcance && (
                <span className="alcance" title={`Solo ves «${yo.alcance.nombre}»`}>
                  <IconoCandado /> {yo.alcance.nombre}
                </span>
              )}
            </span>
          )}
          {yo && (
            <button type="button" className="salir" onClick={() => void salir()}>
              Salir
            </button>
          )}
          </div>
        </div>
      </div>
    </header>
  )
}

const clase = ({ isActive }: { isActive: boolean }) => (isActive ? 'pestaña activa' : 'pestaña')
